import React, {
  FC,
  ReactChild,
  useRef,
  useEffect,
  useCallback,
  ReactNode,
  forwardRef,
  ForwardedRef,
} from "react";
import ReactDOM from "react-dom";
import ReactTooltip from "react-tooltip";
import cx from "classnames";
import { ReactComponent as ArrowIcon } from "./../../assets/images/tooltipArrow.svg";

import styles from "./styles.module.scss";

interface IProps {
  tooltipId: string;
  children: ReactChild;
  content: ReactChild;
  tooltipPosition?: TooltipPosition;
  useClick?: boolean;
  extraHeight?: number;
  useStartPosition?: boolean;
  className?: string;
  isCapture?: boolean;
  onClose?(): void;
  onOpen?(): void;
}

export enum TooltipPosition {
  Left,
  TopLeft,
  TopRight,
  Right,
  BottomRight,
  BottomLeft,
}

const possitions = [
  styles.left,
  styles.topLeft,
  styles.topRight,
  styles.right,
  styles.bottomRight,
  styles.bottomLeft,
];
const startPossitions = [
  styles.startLeft,
  styles.startTopLeft,
  styles.startTopRight,
  styles.startRight,
  styles.startBottomRight,
  styles.startBottomLeft,
];

const getScrollParent = (node: HTMLElement | null): HTMLElement | null => {
  if (node === null) {
    return null;
  }

  if (node.scrollHeight > node.clientHeight) {
    return node;
  } else {
    return getScrollParent(node.parentNode as HTMLElement | null);
  }
};

const elementOutCheker = (
  chekElement: HTMLElement,
  overElement: HTMLElement,
  tooltipElement: HTMLElement
) =>
  !(
    (tooltipElement &&
      (ReactDOM.findDOMNode(tooltipElement)?.contains(chekElement as Node) ||
        chekElement === tooltipElement)) ||
    (overElement &&
      (ReactDOM.findDOMNode(overElement)?.contains(chekElement as Node) ||
        overElement === chekElement))
  );

const Tooltip: FC<IProps> = forwardRef(
  (
    {
      tooltipId,
      content,
      children,
      tooltipPosition = TooltipPosition.Left,
      useClick = false,
      useStartPosition = false,
      className,
      extraHeight = 0,
      onClose,
      onOpen,
    }: IProps,
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    const tooltipWrapper = useRef<HTMLDivElement>(null);
    const tooltipElement = useRef<HTMLDivElement>(null);

    const checkTooltipOver = useCallback(
      (e: Event) => {
        const target = e.target as HTMLElement;
        if (
          tooltipWrapper.current &&
          tooltipElement.current &&
          elementOutCheker(
            target,
            tooltipWrapper.current,
            tooltipElement.current
          )
        )
          ReactTooltip.hide();
      },
      [tooltipWrapper, tooltipElement]
    );

    useEffect(() => {
      return () => {
        if (onClose) onClose();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      const hideTooltip = () => {
        ReactTooltip.hide();
      };
      const wrapper = tooltipWrapper.current;
      const scrollable = getScrollParent(wrapper);

      if (scrollable) scrollable.addEventListener("scroll", hideTooltip);
      return () => {
        if (scrollable) scrollable.removeEventListener("scroll", hideTooltip);
      };
    });

    const root = document.getElementById("root");

    return (
      <>
        {React.Children.map(children, (element: ReactNode) => {
          if (!React.isValidElement(element)) return null;
          const props = element.props;
          const clickOption: {
            ["data-event"]?: string;
            onMouseDown?: () => void;
          } = {};
          if (useClick) {
            clickOption["data-event"] = "click";
            clickOption.onMouseDown = () => {
              ReactTooltip.hide();
            };
          }

          return (
            <element.type
              {...props}
              {...clickOption}
              data-for={tooltipId}
              data-tip
              ref={tooltipWrapper}
            ></element.type>
          );
        })}

        {root &&
          ReactDOM.createPortal(
            <ReactTooltip
              id={tooltipId}
              className={cx(styles.reactTooltip, {
                [possitions[tooltipPosition]]: !useStartPosition,
                [startPossitions[tooltipPosition]]: useStartPosition,
              })}
              effect="solid"
              delayHide={0}
              delayShow={0}
              delayUpdate={100}
              place={"top"}
              clickable={useClick}
              type={"light"}
              afterHide={() => {
                if (onClose) onClose();
                document.removeEventListener("click", checkTooltipOver);
              }}
              afterShow={() => {
                if (onOpen) onOpen();
                if (tooltipWrapper.current !== null) {
                  const tooltip = document.getElementById(tooltipId);

                  if (tooltip) {
                    tooltip.style.setProperty(
                      "--wrapperHeight",
                      `${tooltipWrapper.current.offsetHeight}px`
                    );
                    tooltip.style.setProperty(
                      "--wrapperWidth",
                      `${tooltipWrapper.current.offsetWidth}px`
                    );
                  }
                }

                if (tooltipElement.current !== null) {
                  tooltipElement.current.style.setProperty(
                    "--tooltipHeight",
                    `${tooltipElement.current.clientHeight}px` //tooltipElement.current.clientHeight + extraHeight
                  );
                  tooltipElement.current.style.setProperty(
                    "--tooltipWidth",
                    `${tooltipElement.current.clientWidth}px`
                  );
                  document.addEventListener("click", checkTooltipOver);
                }
              }}
              isCapture={true}
            >
              <div
                ref={ref}
                className={cx(styles.tooltip, {
                  [className!]: className,
                })}
              >
                <div>
                  <ArrowIcon className={styles.arrow} />
                  {content}
                </div>
              </div>
            </ReactTooltip>,
            root
          )}
      </>
    );
  }
);

export default Tooltip;
